import { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { findClosestSampleIndex } from '../utils/splitTimes';
import { convertSpeed, convertDistance, speedUnit, distanceUnit } from '../utils/units';

const PADDING = { top: 32, right: 20, bottom: 30, left: 50 };
const GRID_STEPS = 4;
const X_STEPS = 5;
const CANDLE_BODY_MAX_WIDTH = 10;

const TRACE_COLORS = [
  '#2e86c1', '#e74c3c', '#27ae60', '#f39c12', '#9b59b6',
  '#1abc9c', '#e67e22', '#3498db', '#e91e63', '#00bcd4',
];

function distributeLabelPositions(items, minY, maxY, spacing) {
  if (!items.length) return [];

  const placed = items
    .slice()
    .sort((a, b) => a.desiredY - b.desiredY)
    .map((item) => ({ ...item }));

  placed[0].labelY = Math.max(minY, Math.min(maxY, placed[0].desiredY));

  for (let i = 1; i < placed.length; i++) {
    const minAllowed = placed[i - 1].labelY + spacing;
    placed[i].labelY = Math.max(minAllowed, placed[i].desiredY);
  }

  if (placed[placed.length - 1].labelY > maxY) {
    placed[placed.length - 1].labelY = maxY;
    for (let i = placed.length - 2; i >= 0; i--) {
      placed[i].labelY = Math.min(placed[i].labelY, placed[i + 1].labelY - spacing);
    }
    if (placed[0].labelY < minY) {
      const shift = minY - placed[0].labelY;
      for (let i = 0; i < placed.length; i++) {
        placed[i].labelY += shift;
      }
    }
  }

  return placed;
}

function interpolatePoints(points, sampleCount) {
  if (!Array.isArray(points) || points.length === 0) return [];
  if (points.length === 1) return [{ dist: points[0].dist || 0, value: points[0].value || 0 }];

  const maxDist = points[points.length - 1].dist || 0;
  if (maxDist <= 0) return points.map((p) => ({ dist: p.dist || 0, value: p.value || 0 }));

  const result = [];
  let srcIdx = 1;

  for (let i = 0; i < sampleCount; i++) {
    const targetDist = (i / Math.max(sampleCount - 1, 1)) * maxDist;

    while (srcIdx < points.length && (points[srcIdx].dist || 0) < targetDist) {
      srcIdx += 1;
    }

    const next = points[Math.min(srcIdx, points.length - 1)];
    const prev = points[Math.max(srcIdx - 1, 0)];
    const prevDist = prev.dist || 0;
    const nextDist = next.dist || prevDist;
    const span = nextDist - prevDist || 1;
    const t = Math.min(1, Math.max(0, (targetDist - prevDist) / span));

    result.push({
      dist: targetDist,
      value: (prev.value || 0) + ((next.value || 0) - (prev.value || 0)) * t,
    });
  }

  return result;
}

function computeZoomedRange(zoomRange, fracX, factor) {
  const curX0 = zoomRange ? zoomRange.x0 : 0;
  const curX1 = zoomRange ? zoomRange.x1 : 1;
  const curSpan = curX1 - curX0;
  const anchorX = curX0 + fracX * curSpan;
  const newSpan = curSpan * factor;

  if (newSpan >= 1.0) return null;
  if (newSpan < 0.001) return zoomRange;

  let newX0 = anchorX - fracX * newSpan;
  let newX1 = anchorX + (1 - fracX) * newSpan;

  if (newX0 < 0) { newX1 -= newX0; newX0 = 0; }
  if (newX1 > 1) { newX0 -= (newX1 - 1); newX1 = 1; }
  newX0 = Math.max(0, newX0);
  newX1 = Math.min(1, newX1);

  return { x0: newX0, x1: newX1 };
}

function interpolateValueAtDist(points, targetDist) {
  if (!Array.isArray(points) || points.length === 0) return null;
  if (points.length === 1) {
    return { dist: points[0].dist || 0, value: points[0].value || 0 };
  }

  const maxDist = points[points.length - 1].dist || 0;
  const clamped = Math.max(0, Math.min(targetDist, maxDist));

  let nextIdx = 1;
  while (nextIdx < points.length && (points[nextIdx].dist || 0) < clamped) {
    nextIdx += 1;
  }

  const next = points[Math.min(nextIdx, points.length - 1)];
  const prev = points[Math.max(nextIdx - 1, 0)];
  const prevDist = prev.dist || 0;
  const nextDist = next.dist || prevDist;
  const span = nextDist - prevDist || 1;
  const t = Math.min(1, Math.max(0, (clamped - prevDist) / span));

  return {
    dist: clamped,
    value: (prev.value || 0) + ((next.value || 0) - (prev.value || 0)) * t,
  };
}

function toRgba(color, alpha) {
  if (typeof color !== 'string') return `rgba(255, 255, 255, ${alpha})`;
  // Handle hsl/hsla strings by wrapping with alpha
  if (color.startsWith('hsl')) {
    const match = color.match(/hsl\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*\)/);
    if (match) return `hsla(${match[1]}, ${match[2]}%, ${match[3]}%, ${alpha})`;
    return color;
  }
  // Handle rgb/rgba strings
  if (color.startsWith('rgb')) {
    const match = color.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/);
    if (match) return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
    return color;
  }
  // Handle hex strings
  const clean = color.replace('#', '');
  if (clean.length !== 6) return `rgba(255, 255, 255, ${alpha})`;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function spreadToHeatColor(spread, minSpread, maxSpread) {
  const safeMin = Number.isFinite(minSpread) ? minSpread : 0;
  const safeMax = Number.isFinite(maxSpread) ? maxSpread : safeMin + 1;
  const span = safeMax - safeMin || 1;
  const normalized = Math.max(0, Math.min(1, (spread - safeMin) / span));
  const hue = 120 + 100 * normalized;
  return `hsl(${hue}, 78%, 52%)`;
}

function findNearestCandle(candles, targetDist) {
  if (!Array.isArray(candles) || !candles.length) return null;

  let best = candles[0];
  let bestDelta = Math.abs((candles[0].midDist || 0) - targetDist);

  for (let i = 1; i < candles.length; i += 1) {
    const delta = Math.abs((candles[i].midDist || 0) - targetDist);
    if (delta < bestDelta) {
      best = candles[i];
      bestDelta = delta;
    }
  }

  return best;
}

function quantile(sortedValues, q) {
  if (!Array.isArray(sortedValues) || !sortedValues.length) return 0;
  if (sortedValues.length === 1) return sortedValues[0];

  const clampedQ = Math.max(0, Math.min(1, q));
  const pos = (sortedValues.length - 1) * clampedQ;
  const lower = Math.floor(pos);
  const upper = Math.ceil(pos);

  if (lower === upper) return sortedValues[lower];

  const weight = pos - lower;
  return sortedValues[lower] + (sortedValues[upper] - sortedValues[lower]) * weight;
}

function buildTracePoints(samples, key, xField = 'dist') {
  if (!Array.isArray(samples) || !key) return [];

  return samples
    .map((sample) => {
      const dist = Number(sample?.[xField]);
      const value = Number(sample?.[key]);
      if (!Number.isFinite(dist) || !Number.isFinite(value)) return null;
      return { dist, value };
    })
    .filter(Boolean);
}

function computeAxisDomain({
  traces,
  channel,
  zoomRange,
  yRangeProp,
  isRibbonView,
  ribbonTrace,
  isCandleView,
  candleTrace,
}) {
  // Use one domain calculator for full-lap and zoomed views so the axis
  // responds consistently to the currently visible telemetry values.
  const traceMaxDist = traces.reduce((best, trace) => {
    const lastDist = trace?.points?.[trace.points.length - 1]?.dist;
    return Number.isFinite(lastDist) ? Math.max(best, lastDist) : best;
  }, 0);

  const ribbonMaxDist = ribbonTrace?.profile?.[ribbonTrace.profile.length - 1]?.dist;
  const candleMaxDist = candleTrace?.candles?.[candleTrace.candles.length - 1]?.midDist;
  const totalMaxDist = Math.max(
    traceMaxDist,
    Number.isFinite(ribbonMaxDist) ? ribbonMaxDist : 0,
    Number.isFinite(candleMaxDist) ? candleMaxDist : 0,
    0.25
  );

  const distStart = zoomRange ? zoomRange.x0 * totalMaxDist : 0;
  const distEnd = zoomRange ? zoomRange.x1 * totalMaxDist : totalMaxDist;
  const maxDist = Math.max(distEnd - distStart, 1e-9);

  if (yRangeProp) {
    const range = yRangeProp.max - yRangeProp.min || 1;
    return {
      min: yRangeProp.min,
      max: yRangeProp.max,
      range,
      maxDist,
      distStart,
    };
  }

  let values = [];
  if (isRibbonView) {
    (ribbonTrace?.profile || []).forEach((point) => {
      if (point.dist < distStart || point.dist > distEnd) return;
      [point.min, point.q1, point.median, point.q3, point.max, point.best].forEach((value) => {
        if (Number.isFinite(value)) values.push(value);
      });
    });
  } else if (isCandleView) {
    (candleTrace?.candles || []).forEach((candle) => {
      if (candle.midDist < distStart || candle.midDist > distEnd) return;
      [candle.open, candle.high, candle.low, candle.close].forEach((value) => {
        if (Number.isFinite(value)) values.push(value);
      });
    });
  } else {
    traces.forEach((trace) => {
      (trace?.points || []).forEach((point) => {
        if (point.dist < distStart || point.dist > distEnd) return;
        if (Number.isFinite(point.value)) values.push(point.value);
      });
    });
  }

  if (!values.length) {
    return { min: 0, max: 1, range: 1, maxDist, distStart };
  }

  let min = Math.min(...values);
  let max = Math.max(...values);

  // Speed/RPM should sit slightly below the observed minimum instead of
  // snapping to zero, unless the data itself already goes negative.
  if (channel?.lowerBoundMode === 'tenPercentBelowMinOrZeroIfNegative') {
    min = min < 0 ? 0 : min * 0.9;
  }

  if (channel?.preferZeroMin) {
    min = Math.min(0, min);
  }

  if (channel?.preferZeroMax) {
    max = Math.max(0, max);
  }

  const baseRange = max - min;
  const headroom = channel?.headroom ?? 0.05;
  const pad = baseRange > 0
    ? baseRange * headroom
    : Math.max(Math.abs(max || min) * headroom, channel?.integer ? 1 : 0.5);
  const lowerPad = channel?.preferZeroMin && min === 0 ? 0 : pad;
  const upperPad = channel?.preferZeroMax && max === 0 ? 0 : pad;
  const paddedMin = min - lowerPad;
  const paddedMax = max + upperPad;

  return {
    min: paddedMin,
    max: paddedMax,
    range: paddedMax - paddedMin || 1,
    maxDist,
    distStart,
  };
}

function buildGpsPointCandles(laps, coordinates, valueKey, color) {
  if (!Array.isArray(laps) || !laps.length || !Array.isArray(coordinates) || !coordinates.length || !valueKey) {
    return null;
  }

  const lapSearchStarts = laps.map(() => 0);
  const candles = [];

  for (const coord of coordinates) {
    if (coord?.lat == null || coord?.lng == null) continue;

    const values = [];
    const dists = [];

    laps.forEach((lap, lapIdx) => {
      const samples = lap?.data;
      if (!Array.isArray(samples) || !samples.length) return;

      const match = findClosestSampleIndex(samples, coord.lat, coord.lng, lapSearchStarts[lapIdx], { localSearch: true });
      if (match.index < 0) return;

      lapSearchStarts[lapIdx] = match.index;
      const sample = samples[match.index];
      const value = Number(sample?.[valueKey]);
      const dist = Number(sample?.dist);

      if (!Number.isFinite(value)) return;

      values.push({ value, lapNum: lap?.lapNum ?? null });
      if (Number.isFinite(dist)) dists.push(dist);
    });

    if (!values.length) continue;

    const highEntry = values.reduce((best, entry) => (
      !best || entry.value > best.value ? entry : best
    ), null);
    const lowEntry = values.reduce((best, entry) => (
      !best || entry.value < best.value ? entry : best
    ), null);

    const pointDist = dists.length
      ? dists.reduce((sum, dist) => sum + dist, 0) / dists.length
      : candles[candles.length - 1]?.midDist ?? candles.length;

    candles.push({
      startDist: pointDist,
      endDist: pointDist,
      midDist: pointDist,
      open: values[0].value,
      high: highEntry?.value ?? values[0].value,
      highLapNum: highEntry?.lapNum ?? null,
      low: lowEntry?.value ?? values[0].value,
      lowLapNum: lowEntry?.lapNum ?? null,
      close: values[values.length - 1].value,
    });
  }

  if (!candles.length) return null;

  let minSpread = Number.POSITIVE_INFINITY;
  let maxSpread = Number.NEGATIVE_INFINITY;

  candles.forEach((candle) => {
    const spread = candle.high - candle.low;
    if (spread < minSpread) minSpread = spread;
    if (spread > maxSpread) maxSpread = spread;
  });

  return {
    color,
    label: null,
    points: candles.map((candle) => ({ dist: candle.midDist, value: candle.close })),
    candles,
    minSpread,
    maxSpread,
  };
}

function buildRibbonProfile(laps, coordinates, valueKey, bestLap, color, bestLapColor) {
  if (!Array.isArray(laps) || !laps.length || !Array.isArray(coordinates) || !coordinates.length || !valueKey) {
    return null;
  }

  const lapSearchStarts = laps.map(() => 0);
  let bestLapSearchStart = 0;
  const profile = [];

  for (const coord of coordinates) {
    if (coord?.lat == null || coord?.lng == null) continue;

    const values = [];
    const dists = [];

    laps.forEach((lap, lapIdx) => {
      const samples = lap?.data;
      if (!Array.isArray(samples) || !samples.length) return;

      const match = findClosestSampleIndex(samples, coord.lat, coord.lng, lapSearchStarts[lapIdx], { localSearch: true });
      if (match.index < 0) return;

      lapSearchStarts[lapIdx] = match.index;
      const sample = samples[match.index];
      const value = Number(sample?.[valueKey]);
      const dist = Number(sample?.dist);

      if (!Number.isFinite(value)) return;

      values.push({ value, lapNum: lap?.lapNum ?? null });
      if (Number.isFinite(dist)) dists.push(dist);
    });

    if (!values.length) continue;

    const sorted = values.slice().sort((a, b) => a.value - b.value);
    const pointDist = dists.length
      ? dists.reduce((sum, dist) => sum + dist, 0) / dists.length
      : profile[profile.length - 1]?.dist ?? profile.length;

    let bestValue = null;
    if (bestLap?.data?.length) {
      const bestMatch = findClosestSampleIndex(bestLap.data, coord.lat, coord.lng, bestLapSearchStart, { localSearch: true });
      if (bestMatch.index >= 0) {
        bestLapSearchStart = bestMatch.index;
        const sample = bestLap.data[bestMatch.index];
        const candidate = Number(sample?.[valueKey]);
        if (Number.isFinite(candidate)) {
          bestValue = candidate;
        }
      }
    }

    profile.push({
      dist: pointDist,
      min: sorted[0].value,
      minLapNum: sorted[0].lapNum ?? null,
      q1: quantile(sorted.map((entry) => entry.value), 0.25),
      median: quantile(sorted.map((entry) => entry.value), 0.5),
      q3: quantile(sorted.map((entry) => entry.value), 0.75),
      max: sorted[sorted.length - 1].value,
      maxLapNum: sorted[sorted.length - 1].lapNum ?? null,
      best: bestValue,
    });
  }

  if (!profile.length) return null;

  return {
    color,
    bestLapColor,
    label: null,
    points: profile.map((point) => ({ dist: point.dist, value: point.median })),
    bestPoints: profile
      .filter((point) => Number.isFinite(point.best))
      .map((point) => ({ dist: point.dist, value: point.best })),
    profile,
  };
}

function findNearestRibbonPoint(profile, targetDist) {
  if (!Array.isArray(profile) || !profile.length) return null;

  let best = profile[0];
  let bestDelta = Math.abs((profile[0].dist || 0) - targetDist);

  for (let i = 1; i < profile.length; i += 1) {
    const delta = Math.abs((profile[i].dist || 0) - targetDist);
    if (delta < bestDelta) {
      best = profile[i];
      bestDelta = delta;
    }
  }

  return best;
}

export default function TelemetryGraph({
  // Data input option 1: laps/data + channel
  data,
  laps,
  channel,
  // Data input option 2: pre-computed traces
  precomputedTraces,
  // Layout
  width = 700,
  height = 200,
  samplingMode = 'fit',
  viewStyle = 'standard',
  trackCoordinates,
  // Interaction
  crosshairPos,
  onCrosshairChange,
  interactive,
  zoomRange,
  onZoomChange,
  // Rendering options
  title,
  titleColor,
  yRange: yRangeProp,
  yFormat,
  fillZeroLine,
  crosshairFormat,
  onDragRangeChange,
  segmentBands,
  xAxisMode = 'distance',
  user,
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const xField = xAxisMode === 'time' ? 'time' : 'dist';
  const lastMousePosRef = useRef(null);
  const [displayWidth_, setDisplayWidth_] = useState(width || 700);
  const [dragState, setDragState] = useState(null);

  // Unified trace format: { points: [{dist, value}], color, label }
  const traces = useMemo(() => {
    if (precomputedTraces?.length) {
      return precomputedTraces;
    }
    const result = [];
    const key = channel?.key;
    if (laps?.length && key) {
      laps.forEach((lap, i) => {
        const points = buildTracePoints(lap?.data, key, xField);
        if (points.length) {
          result.push({
            points,
            color: TRACE_COLORS[i % TRACE_COLORS.length],
            label: `L${lap.lapNum}`,
            lapNum: lap.lapNum ?? null,
          });
        }
      });
    } else if (data?.length && key) {
      const points = buildTracePoints(data, key, xField);
      if (points.length) {
        result.push({
          points,
          color: channel?.color || '#2e86c1',
          label: null,
        });
      }
    }
    return result;
  }, [precomputedTraces, laps, data, channel, xField]);

  const renderWidth = Math.max(1, Math.round(width));
  const renderHeight = Math.max(1, Math.round(height));
  const displayWidth = Math.max(1, Math.round(displayWidth_ || renderWidth));
  const displayHeight = renderHeight;
  const plotW = displayWidth - PADDING.left - PADDING.right;
  const plotH = displayHeight - PADDING.top - PADDING.bottom;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const syncDisplayWidth = () => {
      const rect = container.getBoundingClientRect();
      if (rect.width > 0) {
        setDisplayWidth_(rect.width);
      }
    };

    syncDisplayWidth();
    const ro = new ResizeObserver(() => syncDisplayWidth());
    ro.observe(container);
    return () => ro.disconnect();
  }, [renderHeight, renderWidth]);

  const ribbonTrace = useMemo(() => {
    const selectedBestLap = Array.isArray(laps) && laps.length
      ? laps.reduce((best, lap) => (
        !best || (lap?.lapTime ?? Number.POSITIVE_INFINITY) < (best?.lapTime ?? Number.POSITIVE_INFINITY)
          ? lap
          : best
      ), null)
      : null;

    if (viewStyle !== 'ribbon' || !laps?.length || !channel?.key || !trackCoordinates?.length) return null;
    return buildRibbonProfile(
      laps,
      trackCoordinates,
      channel.key,
      selectedBestLap,
      channel.color || '#2e86c1',
      '#f5c542'
    );
  }, [viewStyle, laps, channel, trackCoordinates]);

  const candleTrace = useMemo(() => {
    if (viewStyle !== 'candles' || !laps?.length || !channel?.key || !trackCoordinates?.length) return null;
    return buildGpsPointCandles(laps, trackCoordinates, channel.key, channel.color || '#2e86c1');
  }, [viewStyle, laps, channel, trackCoordinates]);

  const isRibbonView = viewStyle === 'ribbon' && !!ribbonTrace?.profile?.length;
  const isCandleView = viewStyle === 'candles' && !!candleTrace?.candles?.length;

  const axisMeta = useMemo(() => {
    if (!traces.length && !isRibbonView && !isCandleView) {
      return { min: 0, max: 1, range: 1, maxDist: 1, distStart: 0, yLabels: [], xLabels: [] };
    }

    const { min, max, range, maxDist, distStart } = computeAxisDomain({
      traces,
      channel,
      zoomRange,
      yRangeProp,
      isRibbonView,
      ribbonTrace,
      isCandleView,
      candleTrace,
    });

    const baseFmt = yFormat || (channel?.integer ? (v) => Math.round(v).toString() : (v) => v.toFixed(1));
    const fmt = channel?.key === 'speed'
      ? (v) => baseFmt(convertSpeed(v, user))
      : baseFmt;

    const yLabels = Array.from({ length: GRID_STEPS + 1 }, (_, i) => {
      const y = PADDING.top + (i / GRID_STEPS) * plotH;
      const value = max - (i / GRID_STEPS) * range;
      return { key: `y-${i}`, y, value: fmt(value) };
    });

    const xLabels = Array.from({ length: X_STEPS + 1 }, (_, i) => {
      const x = PADDING.left + (i / X_STEPS) * plotW;
      const value = distStart + (i / X_STEPS) * maxDist;
      const label = xAxisMode === 'time'
        ? `${value.toFixed(1)}s`
        : `${convertDistance(value, user).toFixed(2)} ${distanceUnit(user)}`;
      return { key: `x-${i}`, x, value: label };
    });

    return { min, max, range, maxDist, distStart, yLabels, xLabels };
  }, [traces, channel, plotH, plotW, zoomRange, yRangeProp, yFormat, isRibbonView, ribbonTrace, isCandleView, candleTrace, xAxisMode, user]);

  const renderedTraces = useMemo(() => {
    if (isRibbonView) {
      return [ribbonTrace];
    }

    if (!isCandleView) {
      return traces.map((trace) => {
        let points = trace.points;
        if (samplingMode === 'fit') {
          const targetCount = Math.max(points.length, Math.floor(plotW * 1.25));
          if (points.length < targetCount) {
            points = interpolatePoints(points, targetCount);
          }
        }
        return { ...trace, renderPoints: points };
      });
    }

    return [candleTrace];
  }, [traces, ribbonTrace, candleTrace, isRibbonView, isCandleView, samplingMode, plotW]);

  const interactionTraces = isRibbonView || isCandleView ? renderedTraces : traces;

  // Convert displayed value for unit-sensitive channels (speed)
  const displayValue = useCallback((val) => {
    if (val == null) return val;
    if (channel?.key === 'speed') return convertSpeed(val, user);
    return val;
  }, [channel?.key, user]);

  const formatDisplayValue = useCallback((val) => {
    const converted = displayValue(val);
    if (converted == null) return '';
    return channel?.integer ? Math.round(converted).toString() : converted.toFixed(1);
  }, [displayValue, channel?.integer]);

  const crosshairOverlay = useMemo(() => {
    if (crosshairPos == null || crosshairPos < 0 || crosshairPos > 1 || !interactionTraces.length) {
      return null;
    }

    const { min, range, maxDist, distStart } = axisMeta;
    const crosshairX = PADDING.left + crosshairPos * plotW;
    const targetDist = distStart + crosshairPos * maxDist;
    const labelItems = [];

    if (isRibbonView) {
      const profilePoint = findNearestRibbonPoint(renderedTraces[0]?.profile || [], targetDist);
      if (!profilePoint) return null;

      const ribbonX = PADDING.left + ((profilePoint.dist - distStart) / maxDist) * plotW;
      const unitSuffix = channel?.unit ? ` ${channel.unit}` : '';
      const outerColor = '#7aa5d8';
      const innerColor = '#53c9a8';
      const medianColor = renderedTraces[0]?.color || '#2e86c1';
      const bestColor = renderedTraces[0]?.bestLapColor || '#f5c542';

      const ribbonLabels = [
        { value: profilePoint.max, text: 'Max', color: outerColor, lapText: profilePoint.maxLapNum != null ? ` L${profilePoint.maxLapNum}` : '' },
        { value: profilePoint.q3, text: 'Q3', color: innerColor, lapText: '' },
        { value: profilePoint.median, text: 'Median', color: medianColor, lapText: '' },
        { value: profilePoint.q1, text: 'Q1', color: innerColor, lapText: '' },
        { value: profilePoint.min, text: 'Min', color: outerColor, lapText: profilePoint.minLapNum != null ? ` L${profilePoint.minLapNum}` : '' },
        { value: profilePoint.best, text: 'Best', color: bestColor, lapText: '' },
      ].filter(l => Number.isFinite(l.value));

      const ribbonSlotSpacing = plotH / (ribbonLabels.length + 1);
      ribbonLabels.forEach((l, i) => {
        const dotY = PADDING.top + (1 - (l.value - min) / range) * plotH;
        labelItems.push({
          color: l.color,
          textColor: l.color,
          dotY,
          desiredY: PADDING.top + ribbonSlotSpacing * (i + 1),
          text: `${l.text} ${formatDisplayValue(l.value)}${unitSuffix}${l.lapText}`,
        });
      });

      const placedLabels = distributeLabelPositions(
        labelItems,
        PADDING.top + 10,
        PADDING.top + plotH - 4,
        20
      );

      return { crosshairX: ribbonX, targetDist: profilePoint.dist, labels: placedLabels };
    }

    if (isCandleView) {
      const candle = findNearestCandle(renderedTraces[0]?.candles || [], targetDist);
      if (!candle) return null;

      const unitSuffix = channel?.unit ? ` ${channel.unit}` : '';
      const highY = PADDING.top + (1 - (candle.high - min) / range) * plotH;
      const lowY = PADDING.top + (1 - (candle.low - min) / range) * plotH;

      const candleSlot = plotH / 3;
      labelItems.push({
        color: renderedTraces[0]?.color || '#2e86c1',
        textColor: renderedTraces[0]?.color || '#2e86c1',
        dotY: highY,
        desiredY: PADDING.top + candleSlot,
        text: `Peak ${formatDisplayValue(candle.high)}${unitSuffix}${candle.highLapNum != null ? ` L${candle.highLapNum}` : ''}`,
      });

      labelItems.push({
        color: renderedTraces[0]?.color || '#2e86c1',
        textColor: renderedTraces[0]?.color || '#2e86c1',
        dotY: lowY,
        desiredY: PADDING.top + candleSlot * 2,
        text: `Min ${formatDisplayValue(candle.low)}${unitSuffix}${candle.lowLapNum != null ? ` L${candle.lowLapNum}` : ''}`,
      });

      const placedLabels = distributeLabelPositions(
        labelItems,
        PADDING.top + 10,
        PADDING.top + plotH - 4,
        20
      );

      return { crosshairX: PADDING.left + ((candle.midDist - distStart) / maxDist) * plotW, targetDist: candle.midDist, labels: placedLabels };
    }

    const slotSpacing = plotH / (interactionTraces.length + 1);
    interactionTraces.forEach((trace, traceIdx) => {
      const interp = interpolateValueAtDist(trace.points, targetDist);
      if (!interp) return;
      const val = interp.value;
      const dotY = PADDING.top + (1 - (val - min) / range) * plotH;

      const formatted = crosshairFormat
        ? crosshairFormat(val)
        : { text: formatDisplayValue(val) };

      labelItems.push({
        color: trace.color,
        textColor: formatted.color || trace.color,
        dotY,
        desiredY: PADDING.top + slotSpacing * (traceIdx + 1),
        text: `${formatted.text}${trace.lapNum != null ? ` L${trace.lapNum}` : ''}`,
      });
    });

    const placedLabels = distributeLabelPositions(
      labelItems,
      PADDING.top + 10,
      PADDING.top + plotH - 4,
      20
    );

    return { crosshairX, targetDist, labels: placedLabels };
  }, [axisMeta, channel, crosshairPos, isRibbonView, isCandleView, plotH, plotW, interactionTraces, renderedTraces, crosshairFormat, formatDisplayValue]);

  const segmentLabels = useMemo(() => {
    if (!segmentBands?.length || !interactionTraces.length || xAxisMode === 'time') return [];
    const { maxDist, distStart } = axisMeta;
    const distEnd = distStart + maxDist;
    const CHAR_W = 6.5; // approx pixels per char at 10px font
    const labels = segmentBands
      .filter((band) => band.label && band.endDist > distStart && band.startDist < distEnd)
      .map((band, i) => {
        const x0 = PADDING.left + ((Math.max(band.startDist, distStart) - distStart) / maxDist) * plotW;
        const x1 = PADDING.left + ((Math.min(band.endDist, distEnd) - distStart) / maxDist) * plotW;
        const bandW = x1 - x0;
        // Abbreviate label if it won't fit in the band
        let text = band.label;
        if (text.length * CHAR_W > bandW) {
          // "Turn 10 - Whites" → "T10", "Turn 2 - Clayton" → "T2"
          const m = text.match(/^Turn\s+(\d+)/);
          text = m ? `T${m[1]}` : text.split(/\s*-\s*/)[0];
        }
        const labelW = text.length * CHAR_W;
        return { key: `seg-${i}`, x: (x0 + x1) / 2, label: text, labelW, row: 0 };
      });
    // Stagger overlapping labels to a second row
    for (let i = 1; i < labels.length; i++) {
      const prev = labels[i - 1];
      const curr = labels[i];
      const gap = curr.x - prev.x;
      const minGap = (prev.labelW + curr.labelW) / 2 + 4;
      if (gap < minGap && prev.row === curr.row) {
        curr.row = 1;
      }
    }
    return labels;
  }, [segmentBands, axisMeta, plotW, interactionTraces, xAxisMode]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !traces.length) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    canvas.width = Math.round(displayWidth * dpr);
    canvas.height = Math.round(displayHeight * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    canvas.style.width = `${displayWidth}px`;
    canvas.style.height = `${displayHeight}px`;
    ctx.imageSmoothingEnabled = false;

    ctx.fillStyle = '#0a0a10';
    ctx.fillRect(0, 0, displayWidth, displayHeight);

    const { min, range, maxDist, distStart } = axisMeta;

    // Segment bands (distance mode only — bands use distance-based positions)
    if (segmentBands?.length && xAxisMode !== 'time') {
      const distEnd = distStart + maxDist;
      const bandColors = ['#0e0e1a', '#1a1a30'];
      ctx.save();
      ctx.beginPath();
      ctx.rect(PADDING.left, PADDING.top, plotW, plotH);
      ctx.clip();
      segmentBands.forEach((band, i) => {
        if (band.endDist <= distStart || band.startDist >= distEnd) return;
        const x0 = PADDING.left + ((Math.max(band.startDist, distStart) - distStart) / maxDist) * plotW;
        const x1 = PADDING.left + ((Math.min(band.endDist, distEnd) - distStart) / maxDist) * plotW;
        ctx.fillStyle = bandColors[i % 2];
        ctx.fillRect(x0, PADDING.top, x1 - x0, plotH);
      });
      // Segment divider lines
      ctx.strokeStyle = 'rgba(100, 120, 180, 0.35)';
      ctx.lineWidth = 1;
      segmentBands.forEach((band) => {
        if (band.startDist > distStart && band.startDist < distEnd) {
          const x = PADDING.left + ((band.startDist - distStart) / maxDist) * plotW;
          ctx.beginPath();
          ctx.moveTo(x, PADDING.top);
          ctx.lineTo(x, PADDING.top + plotH);
          ctx.stroke();
        }
        if (band.endDist > distStart && band.endDist < distEnd) {
          const x = PADDING.left + ((band.endDist - distStart) / maxDist) * plotW;
          ctx.beginPath();
          ctx.moveTo(x, PADDING.top);
          ctx.lineTo(x, PADDING.top + plotH);
          ctx.stroke();
        }
      });
      ctx.restore();
    }

    // Grid lines
    ctx.strokeStyle = '#1e1e2e';
    ctx.lineWidth = 0.5;
    for (let i = 0; i <= GRID_STEPS; i++) {
      const y = PADDING.top + (i / GRID_STEPS) * plotH;
      ctx.beginPath();
      ctx.moveTo(PADDING.left, y);
      ctx.lineTo(PADDING.left + plotW, y);
      ctx.stroke();
    }

    // Coordinate helpers
    const toX = (dist) => PADDING.left + ((dist - distStart) / maxDist) * plotW;
    const toY = (val) => PADDING.top + (1 - (val - min) / range) * plotH;

    // Clip to plot area
    ctx.save();
    ctx.beginPath();
    ctx.rect(PADDING.left, PADDING.top, plotW, plotH);
    ctx.clip();

    // Fill around zero line (drawn before traces so line is on top)
    if (fillZeroLine && renderedTraces.length > 0) {
      const zeroY = toY(0);

      // Dashed zero reference line
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(PADDING.left, zeroY);
      ctx.lineTo(PADDING.left + plotW, zeroY);
      ctx.stroke();
      ctx.setLineDash([]);

      // Red/green gradient fill using first trace
      const pts = isRibbonView
        ? renderedTraces[0].points || []
        : isCandleView
        ? renderedTraces[0].points || []
        : renderedTraces[0].renderPoints;
      ctx.save();
      if (pts.length > 0) {
        ctx.beginPath();
        ctx.moveTo(toX(pts[0].dist), zeroY);
        for (let i = 0; i < pts.length; i++) {
          ctx.lineTo(toX(pts[i].dist), toY(pts[i].value));
        }
        ctx.lineTo(toX(pts[pts.length - 1].dist), zeroY);
        ctx.closePath();
        ctx.clip();
        ctx.fillStyle = 'rgba(39, 174, 96, 0.15)';
        ctx.fillRect(PADDING.left, zeroY, plotW, PADDING.top + plotH - zeroY);
        ctx.fillStyle = 'rgba(231, 76, 60, 0.15)';
        ctx.fillRect(PADDING.left, PADDING.top, plotW, zeroY - PADDING.top);
      }
      ctx.restore();
    }

    if (isRibbonView) {
      const trace = renderedTraces[0];
      const profile = trace?.profile || [];
      const medianPoints = trace?.points || [];
      const bestPoints = trace?.bestPoints || [];
      const medianColor = trace?.color || '#2e86c1';
      const bestColor = trace?.bestLapColor || '#f5c542';
      const outerFill = toRgba(medianColor, 0.12);
      const innerFill = toRgba(medianColor, 0.28);

      if (profile.length > 1) {
        ctx.fillStyle = outerFill;
        ctx.beginPath();
        ctx.moveTo(toX(profile[0].dist), toY(profile[0].max));
        for (let i = 1; i < profile.length; i++) {
          ctx.lineTo(toX(profile[i].dist), toY(profile[i].max));
        }
        for (let i = profile.length - 1; i >= 0; i--) {
          ctx.lineTo(toX(profile[i].dist), toY(profile[i].min));
        }
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = innerFill;
        ctx.beginPath();
        ctx.moveTo(toX(profile[0].dist), toY(profile[0].q3));
        for (let i = 1; i < profile.length; i++) {
          ctx.lineTo(toX(profile[i].dist), toY(profile[i].q3));
        }
        for (let i = profile.length - 1; i >= 0; i--) {
          ctx.lineTo(toX(profile[i].dist), toY(profile[i].q1));
        }
        ctx.closePath();
        ctx.fill();
      }

      if (medianPoints.length) {
        ctx.strokeStyle = medianColor;
        ctx.lineWidth = 2;
        ctx.beginPath();
        for (let i = 0; i < medianPoints.length; i++) {
          const x = toX(medianPoints[i].dist);
          const y = toY(medianPoints[i].value);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      if (bestPoints.length) {
        ctx.strokeStyle = bestColor;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        for (let i = 0; i < bestPoints.length; i++) {
          const x = toX(bestPoints[i].dist);
          const y = toY(bestPoints[i].value);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.setLineDash([]);
      }
    } else if (isCandleView) {
      const trace = renderedTraces[0];
      const candles = trace?.candles || [];
      const minSpread = trace?.minSpread ?? 0;
      const maxSpread = trace?.maxSpread ?? 1;

      candles.forEach((candle, candleIdx) => {
        const centerX = toX(candle.midDist);
        const prevX = candleIdx > 0 ? toX(candles[candleIdx - 1].midDist) : centerX;
        const nextX = candleIdx < candles.length - 1 ? toX(candles[candleIdx + 1].midDist) : centerX;
        const localSpacing = Math.min(Math.abs(centerX - prevX) || Number.POSITIVE_INFINITY, Math.abs(nextX - centerX) || Number.POSITIVE_INFINITY);
        const bodyWidth = Math.max(1, Math.min(CANDLE_BODY_MAX_WIDTH, Number.isFinite(localSpacing) ? localSpacing * 0.7 : 4));
        const bodyX = centerX - bodyWidth / 2;
        const openY = toY(candle.open);
        const closeY = toY(candle.close);
        const highY = toY(candle.high);
        const lowY = toY(candle.low);
        const bodyTop = Math.min(openY, closeY);
        const bodyBottom = Math.max(openY, closeY);
        const bodyHeight = Math.max(2, bodyBottom - bodyTop);
        const spread = candle.high - candle.low;
        const heatColor = spreadToHeatColor(spread, minSpread, maxSpread);
        const strokeColor = heatColor;
        const fillColor = toRgba(heatColor, 0.3);

        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(centerX, highY);
        ctx.lineTo(centerX, lowY);
        ctx.stroke();

        ctx.fillStyle = fillColor;
        ctx.fillRect(bodyX, bodyTop - (bodyHeight === 2 ? 1 : 0), bodyWidth, bodyHeight);
        ctx.strokeRect(bodyX, bodyTop - (bodyHeight === 2 ? 1 : 0), bodyWidth, bodyHeight);
      });
    } else {
      // Draw traces
      renderedTraces.forEach((trace) => {
        const pts = trace.renderPoints;
        ctx.strokeStyle = trace.color;
        ctx.lineWidth = traces.length > 1 ? 1.2 : 1.5;
        ctx.globalAlpha = traces.length > 3 ? 0.7 : 1;
        ctx.beginPath();
        for (let i = 0; i < pts.length; i++) {
          const x = toX(pts[i].dist);
          const y = toY(pts[i].value);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      });
    }
    ctx.globalAlpha = 1;
    ctx.restore();

    // Crosshair
    if (crosshairOverlay) {
      const chX = crosshairOverlay.crosshairX;

      ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(chX, PADDING.top);
      ctx.lineTo(chX, PADDING.top + plotH);
      ctx.stroke();
      ctx.setLineDash([]);

      crosshairOverlay.labels.forEach((item) => {
        ctx.beginPath();
        ctx.arc(chX, item.dotY, 4, 0, Math.PI * 2);
        ctx.fillStyle = item.color;
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        const labelX = chX + 8;
        if (Math.abs(item.labelY - item.dotY) > 2) {
          ctx.strokeStyle = item.color;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(chX + 5, item.dotY);
          ctx.lineTo(labelX - 3, item.labelY - 3);
          ctx.stroke();
        }
      });
    }

    // Drag selection box
    if (dragState) {
      const { startX, startY, currentX, currentY } = dragState;
      const rx = Math.min(startX, currentX);
      const ry = Math.min(startY, currentY);
      const rw = Math.abs(currentX - startX);
      const rh = Math.abs(currentY - startY);

      ctx.fillStyle = 'rgba(46, 134, 193, 0.15)';
      ctx.fillRect(rx, ry, rw, rh);
      ctx.strokeStyle = 'rgba(46, 134, 193, 0.7)';
      ctx.lineWidth = 1;
      ctx.strokeRect(rx, ry, rw, rh);
    }
  }, [traces, renderedTraces, isRibbonView, isCandleView, axisMeta, displayHeight, displayWidth, plotH, plotW, crosshairOverlay, dragState, fillZeroLine, segmentBands]);

  const clientToRender = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * displayWidth;
    const y = ((e.clientY - rect.top) / rect.height) * displayHeight;
    return { x, y };
  }, [displayWidth, displayHeight]);

  const handleMouseDown = useCallback((e) => {
    if (!onZoomChange) return;
    const pos = clientToRender(e);
    if (!pos) return;
    if (pos.x < PADDING.left || pos.x > PADDING.left + plotW) return;
    if (pos.y < PADDING.top || pos.y > PADDING.top + plotH) return;
    setDragState({ startX: pos.x, startY: pos.y, currentX: pos.x, currentY: pos.y });
  }, [onZoomChange, clientToRender, plotW, plotH]);

  const handleMouseMove = useCallback((e) => {
    if (dragState) {
      const pos = clientToRender(e);
      if (!pos) return;
      const cx = Math.max(PADDING.left, Math.min(PADDING.left + plotW, pos.x));
      const cy = Math.max(PADDING.top, Math.min(PADDING.top + plotH, pos.y));
      setDragState((prev) => prev ? { ...prev, currentX: cx, currentY: cy } : null);

      // Report drag range for track map highlighting
      if (onDragRangeChange) {
        const dxLeft = Math.min(dragState.startX, cx);
        const dxRight = Math.max(dragState.startX, cx);
        const fracX0 = (dxLeft - PADDING.left) / plotW;
        const fracX1 = (dxRight - PADDING.left) / plotW;
        const curX0 = zoomRange ? zoomRange.x0 : 0;
        const curSpan = (zoomRange ? zoomRange.x1 : 1) - curX0;
        onDragRangeChange({
          x0: Math.max(0, curX0 + fracX0 * curSpan),
          x1: Math.min(1, curX0 + fracX1 * curSpan),
        });
      }
      return;
    }

    if (!onCrosshairChange) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const plotLeft = (PADDING.left / displayWidth) * rect.width;
    const plotRight = rect.width - (PADDING.right / displayWidth) * rect.width;
    const pw = plotRight - plotLeft;
    const pos = (mouseX - plotLeft) / pw;
    if (pos >= 0 && pos <= 1) {
      onCrosshairChange(pos);
      lastMousePosRef.current = pos;
    }
  }, [onCrosshairChange, onDragRangeChange, displayWidth, dragState, clientToRender, plotW, plotH, zoomRange]);

  const handleMouseUp = useCallback((e) => {
    if (!dragState || !onZoomChange) {
      setDragState(null);
      if (onDragRangeChange) onDragRangeChange(null);
      return;
    }

    const pos = clientToRender(e);
    if (!pos) {
      setDragState(null);
      return;
    }

    const cx = Math.max(PADDING.left, Math.min(PADDING.left + plotW, pos.x));
    const x0 = Math.min(dragState.startX, cx);
    const x1 = Math.max(dragState.startX, cx);

    if (x1 - x0 < 10) {
      setDragState(null);
      return;
    }

    const fracX0 = (x0 - PADDING.left) / plotW;
    const fracX1 = (x1 - PADDING.left) / plotW;

    const currentX0 = zoomRange ? zoomRange.x0 : 0;
    const currentX1 = zoomRange ? zoomRange.x1 : 1;
    const currentSpan = currentX1 - currentX0;

    const absX0 = currentX0 + fracX0 * currentSpan;
    const absX1 = currentX0 + fracX1 * currentSpan;

    onZoomChange({ x0: Math.max(0, absX0), x1: Math.min(1, absX1) });
    setDragState(null);
    if (onDragRangeChange) onDragRangeChange(null);
  }, [dragState, onZoomChange, onDragRangeChange, clientToRender, plotW, zoomRange]);

  const handleMouseLeave = useCallback(() => {
    lastMousePosRef.current = null;
    if (dragState) {
      setDragState(null);
      if (onDragRangeChange) onDragRangeChange(null);
      return;
    }
    if (interactive && onCrosshairChange) {
      onCrosshairChange(null);
    }
  }, [interactive, onCrosshairChange, onDragRangeChange, dragState]);

  // Wheel zoom — imperative listener so we can preventDefault (React onWheel is passive)
  const wheelHandlerRef = useRef(null);
  wheelHandlerRef.current = useCallback((e) => {
    if (!onZoomChange || dragState) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const rx = ((e.clientX - rect.left) / rect.width) * displayWidth;
    const ry = ((e.clientY - rect.top) / rect.height) * displayHeight;
    if (rx < PADDING.left || rx > PADDING.left + plotW) return;
    if (ry < PADDING.top || ry > PADDING.top + plotH) return;
    e.preventDefault();
    const fracX = (rx - PADDING.left) / plotW;
    const factor = e.deltaY < 0 ? 0.8 : 1.25;
    onZoomChange(computeZoomedRange(zoomRange, fracX, factor));
  }, [onZoomChange, zoomRange, dragState, displayWidth, displayHeight, plotW, plotH]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handler = (e) => wheelHandlerRef.current(e);
    canvas.addEventListener('wheel', handler, { passive: false });
    return () => canvas.removeEventListener('wheel', handler);
  }, []);

  // Keyboard +/- zoom — only responds when mouse is over this chart
  const keyHandlerRef = useRef(null);
  keyHandlerRef.current = useCallback((e) => {
    if (!onZoomChange || lastMousePosRef.current == null) return;
    const isZoomIn = e.key === '+' || e.key === '=';
    const isZoomOut = e.key === '-';
    if (!isZoomIn && !isZoomOut) return;
    e.preventDefault();
    const factor = isZoomIn ? 0.8 : 1.25;
    onZoomChange(computeZoomedRange(zoomRange, lastMousePosRef.current, factor));
  }, [onZoomChange, zoomRange]);

  useEffect(() => {
    const handler = (e) => keyHandlerRef.current(e);
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const displayTitle = title || (channel ? `${channel.label}${channel.unit ? ` (${channel.unit})` : ''}` : '');
  const displayTitleColor = titleColor || channel?.color || traces[0]?.color || '#ccc';
  const legendItems = isRibbonView || isCandleView ? [] : traces.filter((t) => t.label);

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', width: '100%' }}
    >
      <div
        style={{
          position: 'absolute',
          top: 8,
          left: 50,
          right: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        {displayTitle && (
          <div
            style={{
              color: displayTitleColor,
              fontFamily: 'var(--font-heading)',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.6,
              whiteSpace: 'nowrap',
            }}
          >
            {displayTitle}
          </div>
        )}
        {legendItems.length > 1 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 18,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            {legendItems.map((trace) => (
              <div
                key={trace.label}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  color: '#a9b4c2',
                  fontFamily: 'var(--font-body)',
                  fontSize: 12,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                <span
                  style={{
                    width: 22,
                    height: 10,
                    borderRadius: 2,
                    background: trace.color,
                    boxShadow: `0 0 0 1px ${trace.color}33 inset`,
                  }}
                />
                {trace.label}
              </div>
            ))}
          </div>
        )}
      </div>
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        style={{
          display: 'block',
          width: '100%',
          maxWidth: '100%',
          borderRadius: 6,
          border: '1px solid var(--color-border)',
          cursor: dragState ? 'crosshair' : interactive ? 'crosshair' : crosshairPos != null ? 'crosshair' : 'default',
        }}
      />
      {axisMeta.yLabels.map((item) => (
        <div
          key={item.key}
          style={{
            position: 'absolute',
            left: 0,
            top: item.y,
            width: (PADDING.left - 8),
            transform: 'translateY(-50%)',
            color: '#666',
            fontFamily: 'var(--font-body)',
            fontSize: 11,
            fontWeight: 600,
            textAlign: 'right',
            pointerEvents: 'none',
          }}
        >
          {item.value}
        </div>
      ))}
      {axisMeta.xLabels.map((item) => (
        <div
          key={item.key}
          style={{
            position: 'absolute',
            left: item.x,
            bottom: 6,
            transform: 'translateX(-50%)',
            color: '#666',
            fontFamily: 'var(--font-body)',
            fontSize: 11,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          {item.value}
        </div>
      ))}
      {segmentLabels.map((item) => (
        <div
          key={item.key}
          style={{
            position: 'absolute',
            left: item.x,
            top: (PADDING.top + 2 + item.row * 13),
            transform: 'translateX(-50%)',
            color: '#8899aa',
            fontFamily: 'var(--font-body)',
            fontSize: 10,
            fontWeight: 600,
            textShadow: '0 0 3px rgba(0,0,0,0.8)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          {item.label}
        </div>
      ))}
      {crosshairOverlay?.labels.map((item, index) => (
        <div
          key={`crosshair-${index}`}
          style={{
            position: 'absolute',
            left: (crosshairOverlay.crosshairX + 8),
            top: item.labelY,
            transform: 'translateY(-50%)',
            padding: '2px 6px',
            borderRadius: 4,
            background: 'rgba(0, 0, 0, 0.82)',
            color: item.textColor || item.color,
            fontFamily: 'var(--font-data)',
            fontSize: 13,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          {item.text}
        </div>
      ))}
      {crosshairOverlay && (
        <div
          style={{
            position: 'absolute',
            left: crosshairOverlay.crosshairX,
            top: (PADDING.top + plotH + 9),
            transform: 'translateX(-50%)',
            padding: '1px 4px',
            borderRadius: 4,
            background: 'rgba(0, 0, 0, 0.78)',
            color: '#ccc',
            fontFamily: 'var(--font-body)',
            fontSize: 11,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          {convertDistance(crosshairOverlay.targetDist, user).toFixed(2)} {distanceUnit(user)}
        </div>
      )}
    </div>
  );
}
